import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts'
import { supabase } from '../lib/supabase'

const dashStyles = `
  .dash-root { display: flex; flex-direction: column; gap: 20px; }

  /* ===== Hero ===== */
  .dash-hero { position: relative; border-radius: 22px; padding: 32px 32px 28px; color: #fff; overflow: hidden;
    background: linear-gradient(140deg, #3f0d1d 0%, #7f1d1d 48%, #b91c1c 78%, #d63b2a 100%);
    box-shadow: 0 20px 50px rgba(120, 20, 25, 0.30); isolation: isolate; }
  .dash-hero::before { content: ''; position: absolute; inset: 0; z-index: -1;
    background:
      radial-gradient(55% 120% at 92% 0%, rgba(253, 224, 71, 0.14), transparent 55%),
      radial-gradient(50% 110% at 0% 100%, rgba(244, 63, 94, 0.18), transparent 55%); }
  .dash-hero::after { content: ''; position: absolute; left: 12%; right: 12%; bottom: 0; height: 3px; z-index: -1;
    background: linear-gradient(90deg, rgba(255,255,255,0), rgba(255, 236, 229, 0.85), rgba(255,255,255,0));
    border-radius: 999px; }
  .dash-hero-inner { position: relative; z-index: 1; display: flex; flex-direction: column; gap: 20px; }
  .dash-hero-brand { display: inline-flex; align-items: center; gap: 12px; align-self: flex-start; }
  .dash-hero-logo { display: inline-flex; align-items: center; justify-content: center; width: 38px; height: 38px;
    border-radius: 12px; background: linear-gradient(135deg, rgba(255,255,255,0.22), rgba(255,255,255,0.08));
    border: 1px solid rgba(255,255,255,0.32); color: #fff; box-shadow: 0 4px 12px rgba(0,0,0,0.16); }
  .dash-hero-brand-name { font-size: 12px; font-weight: 700; letter-spacing: 0.3em; text-transform: uppercase; color: #ffd7cc; }
  .dash-hero-copy h1 { font-size: clamp(1.45rem, 3.6vw, 2.1rem); font-weight: 800; color: #fff; margin: 0;
    line-height: 1.15; letter-spacing: -0.01em; text-shadow: 0 1px 0 rgba(0,0,0,0.08); }
  .dash-hero-copy p { margin-top: 10px; font-size: 0.92rem; color: #ffd7cc; max-width: 580px; line-height: 1.65; }
  .dash-hero-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin-top: 4px; }
  .dash-hero-btn { display: inline-flex; align-items: center; gap: 9px; padding: 12px 22px; border-radius: 999px;
    border: none; font-weight: 700; font-size: 0.86rem; cursor: pointer; text-decoration: none; min-height: 46px;
    transition: transform .16s, background .16s, box-shadow .16s, color .16s, border-color .16s; }
  .dash-hero-btn-primary { background: #fff; color: #8a1a1a; box-shadow: 0 12px 26px rgba(0,0,0,0.26); }
  .dash-hero-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 16px 30px rgba(0,0,0,0.30); }
  .dash-hero-btn-primary:active { transform: translateY(0); }
  .dash-hero-btn-ghost { background: rgba(255,255,255,0.08); color: #fff; border: 1.5px solid rgba(255,255,255,0.5); }
  .dash-hero-btn-ghost:hover { background: rgba(255,255,255,0.16); border-color: rgba(255,255,255,0.75); }
  .dash-hero-btn-link { background: transparent; border: 1.5px solid transparent; color: #ffd7cc; padding-left: 10px; padding-right: 10px; }
  .dash-hero-btn-link:hover { background: rgba(255,255,255,0.08); color: #fff; }
  .dash-hero-meta { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-top: 10px; }
  .dash-hero-stat { background: rgba(255,255,255,0.09); border: 1px solid rgba(255,255,255,0.2);
    backdrop-filter: blur(8px); border-radius: 16px; padding: 16px 18px; display: flex; flex-direction: column; gap: 5px;
    transition: background .16s, transform .16s; }
  .dash-hero-stat:hover { background: rgba(255,255,255,0.14); transform: translateY(-1px); }
  .dash-hero-stat-label { font-size: 11px; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase; color: #ffd7cc; }
  .dash-hero-stat-value { font-size: 1.7rem; font-weight: 800; color: #fff; line-height: 1.1; }

  /* ===== Hero: optimizado para móvil ===== */
  @media (max-width: 640px) {
    .dash-root { gap: 14px; }
    .dash-hero { padding: 20px 18px 18px; border-radius: 16px; }
    .dash-hero-copy h1 { font-size: 1.35rem; }
    .dash-hero-copy p { font-size: 0.85rem; }
    .dash-hero-actions { flex-direction: column; align-items: stretch; gap: 10px; }
    .dash-hero-btn { width: 100%; justify-content: center; padding: 13px 16px; }
    .dash-hero-meta { grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 8px; }
    .dash-hero-stat { padding: 12px 10px; border-radius: 12px; gap: 3px; }
    .dash-hero-stat-label { font-size: 9.5px; letter-spacing: 0.06em; }
    .dash-hero-stat-value { font-size: 1.25rem; }
  }

  /* ===== Grids ===== */
  .dash-grid { display: grid; grid-template-columns: 1fr; gap: 14px; }
  @media (min-width: 640px) { .dash-grid.kpis { grid-template-columns: repeat(2, 1fr); } }
  @media (min-width: 1024px) {
    .dash-grid.kpis { grid-template-columns: repeat(3, 1fr); }
    .dash-grid.charts { grid-template-columns: 1fr 1.6fr; }
    .dash-grid.lists { grid-template-columns: 1fr 1fr; }
  }

  /* ===== Cards ===== */
  .dash-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 16px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06); padding: 18px; }
  .dash-card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; margin-bottom: 14px; }
  .dash-card-head h2 { font-size: 1.05rem; font-weight: 700; color: #111827; margin: 0; }
  .dash-card-head p { font-size: 0.8rem; color: #6b7280; margin-top: 2px; }

  /* ===== KPI ===== */
  .dash-kpi { display: flex; align-items: center; gap: 16px; transition: transform .15s, box-shadow .15s; }
  .dash-kpi:hover { transform: translateY(-2px); box-shadow: 0 10px 24px rgba(0,0,0,0.09); }
  .dash-kpi-icon { width: 48px; height: 48px; border-radius: 13px; display: flex; align-items: center;
    justify-content: center; color: #fff; flex-shrink: 0; box-shadow: 0 4px 10px rgba(0,0,0,0.12); }
  .dash-kpi-label { font-size: 0.8rem; color: #6b7280; font-weight: 600; }
  .dash-kpi-value { font-size: 1.85rem; font-weight: 800; color: #111827; line-height: 1.15; }
  .dash-kpi-caption { font-size: 0.78rem; color: #9ca3af; }

  /* ===== Pills & badges ===== */
  .dash-pill { display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px; border-radius: 999px;
    font-size: 0.78rem; font-weight: 700; white-space: nowrap; }
  .dash-pill-green { background: #ecfdf5; color: #047857; }
  .dash-badge { font-size: 0.72rem; font-weight: 700; padding: 4px 10px; border-radius: 999px; white-space: nowrap; }
  .dash-badge-pendiente { background: #fef3c7; color: #92400e; }
  .dash-badge-aprobada, .dash-badge-completada, .dash-badge-entregada { background: #ecfdf5; color: #047857; }
  .dash-badge-en_proceso { background: #dbeafe; color: #1e40af; }
  .dash-badge-asignada { background: #ede9fe; color: #5b21b6; }
  .dash-badge-rechazada { background: #fee2e2; color: #991b1b; }
  .dash-prioridad { font-size: 0.72rem; font-weight: 700; padding: 4px 10px; border-radius: 999px; white-space: nowrap; }
  .dash-prioridad-critica { background: #fee2e2; color: #991b1b; }
  .dash-prioridad-alta { background: #ffedd5; color: #c2410c; }
  .dash-prioridad-media { background: #fef3c7; color: #b45309; }
  .dash-prioridad-baja { background: #d1fae5; color: #065f46; }
  .dash-prioridad-sin { background: #f3f4f6; color: #6b7280; }

  /* ===== Charts ===== */
  .dash-chart { height: 250px; width: 100%; }
  .dash-legend { display: flex; flex-wrap: wrap; gap: 14px; margin-top: 8px; }
  .dash-legend span { display: inline-flex; align-items: center; gap: 6px; font-size: 0.78rem; color: #374151; }
  .dash-dot { width: 10px; height: 10px; border-radius: 999px; display: inline-block; }

  /* ===== Lists ===== */
  .dash-list { display: flex; flex-direction: column; gap: 10px; }
  .dash-item { display: flex; align-items: center; justify-content: space-between; gap: 10px;
    background: #f9fafb; border: 1px solid #f3f4f6; border-radius: 12px; padding: 12px 14px; }
  .dash-item b { display: block; font-size: 0.9rem; color: #111827; }
  .dash-item small { font-size: 0.78rem; color: #6b7280; }
  .dash-empty { text-align: center; color: #9ca3af; font-size: 0.85rem; padding: 22px 10px;
    border: 1px dashed #e5e7eb; border-radius: 12px; }

  /* ===== Skeleton ===== */
  .dash-skeleton { display: flex; flex-direction: column; gap: 12px; }
  .dash-skel { border-radius: 10px; background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
    background-size: 200% 100%; animation: dash-shimmer 1.2s infinite; }
  @keyframes dash-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
`

const Dashboard = () => {
  const [stats, setStats] = useState({
    total_afectados: 0,
    solicitudes_atendidas: 0,
    solicitudes_pendientes: 0,
    donaciones_realizadas: 0,
    voluntarios_activos: 0,
    rutas_activas: 0
  })
  const [loading, setLoading] = useState(true)
  const [ultimasSolicitudes, setUltimasSolicitudes] = useState([])
  const [ultimosAfectados, setUltimosAfectados] = useState([])
  const [resumenPrioridades, setResumenPrioridades] = useState([])

  useEffect(() => {
    cargarResumen()
  }, [])

  const cargarResumen = async () => {
    try {
      setLoading(true)

      const [afectadosRes, solicitudesRes, donacionesRes, voluntariosRes, rutasActivasRes, solicitudesRecientesRes, afectadosRecientesRes] = await Promise.all([
        supabase.from('afectados').select('*', { count: 'exact', head: true }),
        supabase.from('afectados_solicitudes').select('id, codigo, estado, prioridad, created_at'),
        supabase.from('donaciones').select('*', { count: 'exact', head: true }).in('estado', ['asignada', 'entregada']),
        supabase.from('voluntarios').select('*', { count: 'exact', head: true }).eq('activo', true),
        supabase.from('rdo_seguimiento').select('*', { count: 'exact', head: true }).eq('estado', 'en_ruta'),
        supabase.from('afectados_solicitudes').select('id, codigo, estado, prioridad, created_at').order('created_at', { ascending: false }).limit(6),
        supabase.from('afectados').select('id, codigo, estado, created_at, datos').order('created_at', { ascending: false }).limit(6)
      ])

      const solicitudesData = solicitudesRes.data || []
      const prioridades = ['critica', 'alta', 'media', 'baja']

      setStats({
        total_afectados: afectadosRes.count || 0,
        solicitudes_atendidas: solicitudesData.filter((s) => ['aprobada', 'en_proceso', 'completada'].includes(s.estado)).length,
        solicitudes_pendientes: solicitudesData.filter((s) => s.estado === 'pendiente').length,
        donaciones_realizadas: donacionesRes.count || 0,
        voluntarios_activos: voluntariosRes.count || 0,
        rutas_activas: rutasActivasRes.count || 0
      })

      setResumenPrioridades(
        prioridades.map((prioridad) => ({
          label: prioridad.charAt(0).toUpperCase() + prioridad.slice(1),
          valor: solicitudesData.filter((s) => s.prioridad === prioridad).length
        })).filter((item) => item.valor > 0)
      )

      setUltimasSolicitudes(solicitudesRecientesRes.data || [])
      setUltimosAfectados(afectadosRecientesRes.data || [])
    } catch (error) {
      console.error('Error cargando resumen del dashboard:', error)
    } finally {
      setLoading(false)
    }
  }

  const getNombreAfectado = (afectado) => {
    return afectado?.datos?.['Nombres y Apellidos'] || afectado?.codigo || 'Sin nombre'
  }

  const getEstadoLabel = (estado) => {
    const mapa = {
      pendiente: 'Pendiente',
      aprobada: 'Aprobada',
      en_proceso: 'En proceso',
      completada: 'Completada',
      entregada: 'Entregada',
      asignada: 'Asignada',
      rechazada: 'Rechazada'
    }
    return mapa[estado] || estado || 'Sin estado'
  }

  const compliance = useMemo(() => {
    const total = stats.solicitudes_atendidas + stats.solicitudes_pendientes
    return total > 0 ? Math.round((stats.solicitudes_atendidas / total) * 100) : 0
  }, [stats])

  const pieData = useMemo(() => [
    { name: 'Atendidas', value: stats.solicitudes_atendidas, color: '#dc2626' },
    { name: 'Pendientes', value: stats.solicitudes_pendientes, color: '#f59e0b' }
  ], [stats])

  const barData = useMemo(() => {
    const colorMap = { Critica: '#dc2626', Alta: '#f97316', Media: '#f59e0b', Baja: '#10b981' }
    return resumenPrioridades.length > 0
      ? resumenPrioridades.map((p) => ({ ...p, color: colorMap[p.label] || '#dc2626' }))
      : [{ label: 'Sin datos', valor: 0, color: '#e5e7eb' }]
  }, [resumenPrioridades])

  const tarjetas = [
    { titulo: 'Afectados registrados', valor: stats.total_afectados, descripcion: 'Personas inscritas', color: 'linear-gradient(135deg,#dc2626,#991b1b)', icon: 'people' },
    { titulo: 'Solicitudes atendidas', valor: stats.solicitudes_atendidas, descripcion: 'En ejecución o cerradas', color: 'linear-gradient(135deg,#3b82f6,#1d4ed8)', icon: 'check' },
    { titulo: 'Donaciones realizadas', valor: stats.donaciones_realizadas, descripcion: 'Despachadas o asignadas', color: 'linear-gradient(135deg,#10b981,#047857)', icon: 'box' },
    { titulo: 'Voluntarios activos', valor: stats.voluntarios_activos, descripcion: 'Equipo disponible', color: 'linear-gradient(135deg,#f59e0b,#d97706)', icon: 'volunteer' },
    { titulo: 'Solicitudes pendientes', valor: stats.solicitudes_pendientes, descripcion: 'Por revisar', color: 'linear-gradient(135deg,#f97316,#ea580c)', icon: 'time' },
    { titulo: 'Rutas activas', valor: stats.rutas_activas, descripcion: 'Entregas en curso', color: 'linear-gradient(135deg,#8b5cf6,#6d28d9)', icon: 'route' }
  ]

  const renderIcon = (icon) => {
    switch (icon) {
      case 'people':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="11" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        )
      case 'check':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        )
      case 'box':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0-2 0l7-4A2 2 0 0 0 21 16Z" />
            <path d="m3.29 7 8.71 5 8.71-5" />
            <path d="M12 22V12" />
          </svg>
        )
      case 'volunteer':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Z" />
            <path d="M15 20a3 3 0 0 0 3-3" />
            <path d="M9 20a3 3 0 0 1-3-3" />
            <path d="M7 13.5A4.5 4.5 0 0 1 11.5 9" />
            <path d="M16 9.5a4.5 4.5 0 0 0-4.5 4.5" />
          </svg>
        )
      case 'time':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
        )
      case 'route':
        return (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 17 8 7l4 6 5-8 4 12" />
            <path d="M3 17h18" />
          </svg>
        )
      default:
        return null
    }
  }

  return (
    <>
      <style>{dashStyles}</style>
      <div className="dash-root">
        {/* ===== Hero ===== */}
        <div className="dash-hero">
          <div className="dash-hero-inner">
            <div className="dash-hero-brand">
              <span className="dash-hero-logo">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                </svg>
              </span>
              <span className="dash-hero-brand-name">Rutas de Solidaridad</span>
            </div>

            <div className="dash-hero-copy">
              <h1>Panel de seguimiento para voluntarios</h1>
              <p>Monitorea el avance de solicitudes, entregas y el apoyo a personas afectadas, todo desde un solo lugar.</p>
            </div>

            <div className="dash-hero-actions">
              <Link to="/afectados/registro" className="dash-hero-btn dash-hero-btn-ghost">＋ Registrar afectado</Link>
              <Link to="/afectados/solicitudes" className="dash-hero-btn dash-hero-btn-ghost">Ver solicitudes</Link>
              <Link to="/inventario/donaciones" className="dash-hero-btn dash-hero-btn-ghost">Recibir donación</Link>
            </div>

            <div className="dash-hero-meta">
              <div className="dash-hero-stat">
                <span className="dash-hero-stat-label">Cobertura actual</span>
                <b className="dash-hero-stat-value">{compliance}%</b>
              </div>
              <div className="dash-hero-stat">
                <span className="dash-hero-stat-label">Solicitudes pendientes</span>
                <b className="dash-hero-stat-value">{stats.solicitudes_pendientes}</b>
              </div>
              <div className="dash-hero-stat">
                <span className="dash-hero-stat-label">Rutas activas</span>
                <b className="dash-hero-stat-value">{stats.rutas_activas}</b>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="dash-grid kpis">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="dash-card">
                <div className="dash-skeleton">
                  <div className="dash-skel" style={{ height: 56 }} />
                  <div className="dash-skel" style={{ height: 18 }} />
                  <div className="dash-skel" style={{ height: 14, width: '60%' }} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* ===== KPIs ===== */}
            <div className="dash-grid kpis">
              {tarjetas.map((item) => (
                <div key={item.titulo} className="dash-card dash-kpi">
                  <div className="dash-kpi-icon" style={{ background: item.color }}>{renderIcon(item.icon)}</div>
                  <div>
                    <div className="dash-kpi-label">{item.titulo}</div>
                    <div className="dash-kpi-value">{item.valor}</div>
                    <div className="dash-kpi-caption">{item.descripcion}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* ===== Charts ===== */}
            <div className="dash-grid charts">
              <div className="dash-card">
                <div className="dash-card-head">
                  <div>
                    <h2>Estado de avance</h2>
                    <p>Eficiencia de atención</p>
                  </div>
                  <span className="dash-pill dash-pill-green">{compliance}%</span>
                </div>
                <div className="dash-chart">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={56} outerRadius={82} paddingAngle={3} strokeWidth={0}>
                        <Cell fill="#dc2626" />
                        <Cell fill="#f59e0b" />
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="dash-legend">
                  <span><i className="dash-dot" style={{ background: '#dc2626' }} /> Atendidas ({stats.solicitudes_atendidas})</span>
                  <span><i className="dash-dot" style={{ background: '#f59e0b' }} /> Pendientes ({stats.solicitudes_pendientes})</span>
                </div>
              </div>

              <div className="dash-card">
                <div className="dash-card-head">
                  <div>
                    <h2>Prioridades de solicitudes</h2>
                    <p>Distribución por urgencia</p>
                  </div>
                </div>
                <div className="dash-chart">
                  <ResponsiveContainer>
                    <BarChart data={barData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} width={28} />
                      <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                      <Bar dataKey="valor" radius={[8, 8, 0, 0]} maxBarSize={48}>
                        {barData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* ===== Lists ===== */}
            <div className="dash-grid lists">
              <div className="dash-card">
                <div className="dash-card-head">
                  <div>
                    <h2>Últimas solicitudes</h2>
                    <p>Actividad reciente del equipo</p>
                  </div>
                </div>
                <div className="dash-list">
                  {ultimasSolicitudes.length === 0 ? (
                    <div className="dash-empty">No hay solicitudes recientes.</div>
                  ) : (
                    ultimasSolicitudes.map((solicitud) => (
                      <div key={solicitud.id} className="dash-item">
                        <div>
                          <b>{solicitud.codigo || `Solicitud ${solicitud.id}`}</b>
                          <small>{getEstadoLabel(solicitud.estado)}</small>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 }}>
                          <span className={`dash-prioridad dash-prioridad-${solicitud.prioridad || 'sin'}`}>
                            {solicitud.prioridad || 'Sin prioridad'}
                          </span>
                          <span className={`dash-badge dash-badge-${solicitud.estado || ''}`}>{getEstadoLabel(solicitud.estado)}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="dash-card">
                <div className="dash-card-head">
                  <div>
                    <h2>Afectados recientes</h2>
                    <p>Nuevos registros del día a día</p>
                  </div>
                </div>
                <div className="dash-list">
                  {ultimosAfectados.length === 0 ? (
                    <div className="dash-empty">No hay afectados recientes.</div>
                  ) : (
                    ultimosAfectados.map((afectado) => (
                      <div key={afectado.id} className="dash-item">
                        <div>
                          <b>{getNombreAfectado(afectado)}</b>
                          <small>{afectado.codigo || 'Sin código'}</small>
                        </div>
                        <span className={`dash-badge dash-badge-${afectado.estado || ''}`}>{getEstadoLabel(afectado.estado)}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default Dashboard